import { SaveOperation } from '@infuse/api/common';
import { UpdateUser, User } from '@infuse/iam/user/type';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

export const useGenericUpdateUser = ({
  apiFn,
  roleApiFn,
  forcedParentId
}: {
  apiFn: SaveOperation<Omit<UpdateUser, 'roles' | 'email'>, User>;
  roleApiFn: SaveOperation<{ roleIds: string[]; replace: boolean; notifyUser?: boolean }, never>;
  forcedParentId?: string;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<unknown, unknown, { parentId?: string; id: string; user: UpdateUser }>({
    mutationFn: async ({ parentId, id, user }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }

      try {
        const updatedUser = await apiFn(parentId ? { id: parentId, userId: id } : { userId: id }, {
          name: user.name,
          version: user.version
        });

        // Only update roles if they are explicitly provided
        if (user.roles && Array.isArray(user.roles)) {
          await roleApiFn(
            { id: parentId, userId: updatedUser.id },
            {
              roleIds: user.roles,
              replace: true,
              notifyUser: true
            }
          );
        }

        notify({
          title: t('iam.users.header.update_user'),
          message: t('iam.users.notify.update_user_success')
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.users.header.update_user'),
          message: getAxiosErrorMessage(e, t('iam.users.notify.update_user_failed'))
        });
        throw e;
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });

  return {
    ...mutation
  };
};
